import { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '@/hooks/useAuth';

export default function ResetPasswordPage() {
  const { updatePassword, user } = useAuth();
  const navigate = useNavigate();
  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [done, setDone] = useState(false);
  const [checking, setChecking] = useState(true);

  useEffect(() => {
    // Give the client a moment to pick up the recovery session from the URL.
    const t = setTimeout(() => setChecking(false), 800);
    return () => clearTimeout(t);
  }, []);

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setError(null);

    if (password.length < 8) {
      setError('Password must be at least 8 characters long.');
      return;
    }
    if (password !== confirm) {
      setError('Passwords do not match.');
      return;
    }

    setSubmitting(true);
    try {
      const { error: err } = await updatePassword(password);
      if (err) {
        setError(err);
      } else {
        setDone(true);
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Something went wrong');
    } finally {
      setSubmitting(false);
    }
  };

  if (checking) {
    return (
      <div className="min-h-screen bg-background-100 flex items-center justify-center">
        <div className="text-center">
          <i className="ri-loader-4-line animate-spin text-3xl text-primary-500 inline-block" aria-hidden="true" />
          <p className="mt-3 text-sm text-foreground-500">Verifying your session...</p>
        </div>
      </div>
    );
  }

  if (!user) {
    return (
      <div className="min-h-screen bg-background-100 flex items-center justify-center px-4">
        <div className="w-full max-w-md bg-white rounded-xl border border-background-200 p-8 text-center">
          <div className="w-16 h-16 flex items-center justify-center rounded-full bg-red-100 text-red-500 mx-auto mb-4">
            <i className="ri-error-warning-line text-3xl" aria-hidden="true" />
          </div>
          <h2 className="font-heading font-bold text-xl text-foreground-900 mb-2">Link Expired or Invalid</h2>
          <p className="text-sm text-foreground-500 leading-relaxed mb-6">
            Your password reset link has expired or is invalid. Please request a new link.
          </p>
          <Link to="/admin/forgot-password" className="btn-primary w-full justify-center">
            Request a New Link
          </Link>
        </div>
      </div>
    );
  }

  if (done) {
    return (
      <div className="min-h-screen bg-background-100 flex items-center justify-center px-4">
        <div className="w-full max-w-md bg-white rounded-xl border border-background-200 p-8 text-center">
          <div className="w-16 h-16 flex items-center justify-center rounded-full bg-green-100 text-green-600 mx-auto mb-4">
            <i className="ri-check-line text-3xl" aria-hidden="true" />
          </div>
          <h2 className="font-heading font-bold text-xl text-foreground-900 mb-2">Password Updated</h2>
          <p className="text-sm text-foreground-500 leading-relaxed mb-6">
            Your password has been changed successfully. Please sign in with your new password.
          </p>
          <button
            type="button"
            onClick={() => navigate('/admin/login', { replace: true })}
            className="btn-primary w-full justify-center"
          >
            Go to Sign In
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background-100 flex items-center justify-center px-4 py-10">
      <div className="w-full max-w-md">
        <div className="text-center mb-8">
          <div className="inline-flex items-center gap-3">
            <span className="w-12 h-12 rounded-full bg-primary-500 text-white flex items-center justify-center font-heading font-bold">
              TG
            </span>
            <div className="text-left">
              <div className="font-heading font-bold text-xl text-foreground-900">Set New Password</div>
              <div className="text-xs text-foreground-500">TGMCI Admin</div>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-xl border border-background-200 p-8">
          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label className="block text-xs font-label font-medium text-foreground-700 mb-1.5">
                New Password
              </label>
              <input
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                autoComplete="new-password"
                className="w-full px-3.5 py-2.5 rounded-md text-sm border border-background-300 outline-none focus:ring-2 focus:ring-primary-300"
                placeholder="At least 8 characters"
              />
            </div>

            <div>
              <label className="block text-xs font-label font-medium text-foreground-700 mb-1.5">
                Confirm New Password
              </label>
              <input
                type="password"
                value={confirm}
                onChange={(e) => setConfirm(e.target.value)}
                required
                autoComplete="new-password"
                className="w-full px-3.5 py-2.5 rounded-md text-sm border border-background-300 outline-none focus:ring-2 focus:ring-primary-300"
                placeholder="Re-enter new password"
              />
            </div>

            {error && (
              <div className="px-3 py-2.5 rounded-md bg-red-50 text-red-600 text-sm border border-red-200">
                {error}
              </div>
            )}

            <button type="submit" disabled={submitting} className="btn-primary w-full justify-center">
              {submitting ? 'Updating...' : 'Update Password'}
            </button>
          </form>
        </div>

        <div className="text-center mt-6">
          <Link to="/admin/login" className="text-sm text-foreground-500 hover:text-primary-600 transition-colors">
            <i className="ri-arrow-left-line mr-1" aria-hidden="true" />
            Back to Sign In
          </Link>
        </div>
      </div>
    </div>
  );
}